import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  Column,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
} from "@tanstack/react-table"
import { cn } from "@/lib/utils"
import { daysUntilDeadline, isBidOverdue, withDeadlineTime } from "@/lib/bid-deadline"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Search,
  MoreVertical,
  Download,
  ChevronDown,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Clock,
  FolderOpen,
  Columns3,
  History,
  Trash2,
  Archive,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useBidsStore } from "@/stores/bids-store"
import { useAuthStore } from "@/stores/auth-store"
import { CreateBidDialog } from "@/components/CreateBidDialog"
import { EditBidDialog } from "@/components/EditBidDialog"
import { DeleteBidDialog } from "@/components/DeleteBidDialog"
import { BidsFilterPopover, type BidsFilters } from "@/components/BidsFilterPopover"
import { RecentlyDeletedBidsDialog } from "@/components/RecentlyDeletedBidsDialog"
import { ClientFilterCombobox } from "@/components/ClientFilterCombobox"
import { getUserDisplayName } from "@/lib/user-display"
import { priorityConfig } from "@/lib/status-config"
import { DotPill } from "@/components/ui/dot-pill"
import { BulkActionsBar } from "@/components/ui/bulk-actions-bar"
import { BidStagePill } from "@/components/dashboard/BidStagePill"
import { BidsLifecycleTabs } from "@/components/BidsLifecycleTabs"
import {
  BIDS_TAB_PARAM,
  bidsTabParam,
  parseBidsTab,
  LIFECYCLE_LABELS,
  OUTCOME_LABELS,
  STAGE_LABELS,
  bidLifecycleOf,
  bidPositionRank,
  type BidLifecycle,
} from "@/lib/bid-lifecycle"
import type { Bid } from "@/lib/schemas"
import { toast } from "sonner"

/**
 * Columns hidden until a person shows them (R4 plan §2.4). Merged UNDER the
 * saved choices, so anyone who already toggled one keeps what they chose.
 */
const DEFAULT_HIDDEN_COLUMNS: VisibilityState = { tags: false, priority: false, value: false }

// Sortable column header. Progressive disclosure: no icon at rest, a faint
// chevron on hover/focus, and a visible one only on the column actually sorted.
function SortableHeader({
  column,
  children,
}: {
  column: Column<Bid, unknown>
  children: React.ReactNode
}) {
  const sorted = column.getIsSorted()
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(sorted === "asc")}
      className={cn(
        "group -ml-3 h-8 px-3 hover:bg-transparent",
        sorted ? "font-medium text-foreground" : "font-normal"
      )}
    >
      {children}
      <ChevronDown
        aria-hidden="true"
        className={cn(
          "ml-1.5 h-3.5 w-3.5 shrink-0 transition-[opacity,transform] duration-150",
          sorted === "asc" && "rotate-180",
          sorted
            ? "text-muted-foreground opacity-100"
            : "text-subtle opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
        )}
      />
    </Button>
  )
}

// Format value for display (e.g., 450000 -> "$450,000")
function formatValue(value: number | undefined): string {
  if (value === undefined || value === null) return "TBD"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value)
}

// Format deadline for display (e.g., "2024-03-15T00:00:00Z" -> "Mar 15, 2024")
function formatDeadline(deadline: string | undefined): string {
  if (!deadline) return "No deadline"
  try {
    const date = new Date(deadline)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  } catch {
    return "Invalid date"
  }
}

export type DeadlineUrgency = 'overdue' | 'urgent' | 'normal' | 'far'

export function getRelativeDeadline(
  deadline: string,
  isOverdue: boolean
): { label: string; urgency: DeadlineUrgency } | null {
  const diffDays = daysUntilDeadline(deadline)
  if (diffDays === null) return null

  if (isOverdue) {
    // A deadline with a time is overdue from that moment, while its day is
    // still today: no whole day has passed yet.
    if (diffDays >= 0) return { label: 'Overdue', urgency: 'overdue' }
    const overdueDays = Math.max(1, Math.abs(diffDays))
    return {
      label: overdueDays === 1 ? '1 day overdue' : `${overdueDays} days overdue`,
      urgency: 'overdue',
    }
  }
  // Past deadline but not flagged overdue (e.g. a submitted/won/lost bid) — no
  // urgency decoration; the caller falls back to the plain formatted date.
  if (diffDays < 0) return null
  if (diffDays === 0) return { label: 'Today', urgency: 'urgent' }
  if (diffDays === 1) return { label: 'Tomorrow', urgency: 'urgent' }
  if (diffDays < 7) return { label: `in ${diffDays} days`, urgency: 'urgent' }
  if (diffDays < 14) return { label: 'in 1 week', urgency: 'normal' }
  if (diffDays < 30) return { label: `in ${Math.round(diffDays / 7)} weeks`, urgency: 'normal' }
  if (diffDays < 60) return { label: 'in 1 month', urgency: 'far' }
  if (diffDays < 365) return { label: `in ${Math.round(diffDays / 30)} months`, urgency: 'far' }
  const years = Math.round(diffDays / 365)
  return { label: `in ${years} year${years === 1 ? '' : 's'}`, urgency: 'far' }
}

// CSV Export Functions
function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/** The CSV rows for an export (exported for tests). */
export function bidsToCsv(bids: Bid[]): string {
  const headers = ['Title', 'Client', 'Lifecycle', 'Stage', 'Outcome', 'Priority', 'Value', 'Deadline', 'Tags', 'Created']
  const rows = bids.map((bid) => {
    const position = bidLifecycleOf(bid)
    return [
    csvEscape(bid.title),
    csvEscape(bid.client),
    LIFECYCLE_LABELS[position.lifecycle],
    position.stage ? STAGE_LABELS[position.stage] : '',
    position.outcome ? csvEscape(OUTCOME_LABELS[position.outcome]) : '',
    bid.priority,
    bid.value?.toString() ?? '',
    bid.deadline ? csvEscape(withDeadlineTime(new Date(bid.deadline).toLocaleDateString(), bid)) : '',
    (bid.tags ?? []).join('; '),
    new Date(bid.createdAt).toLocaleDateString(),
    ]
  })
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
}

/** `bids-export-2026-10-14.csv`, by the user's own calendar day, not UTC's (exported for tests). */
export function bidsCsvFileName(now: Date): string {
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `bids-export-${yyyy}-${mm}-${dd}.csv`
}

interface BidsFooterInput {
  page: number
  pageSize: number
  /** The server's total under the current tab and filters. */
  total: number
  hasMore: boolean
  /** Rows the server returned for this page. */
  pageCount: number
  /** Rows left after the client-side narrowing (search, client). */
  shownCount: number
}

const bidsNoun = (n: number) => (n === 1 ? 'bid' : 'bids')

/**
 * One footer format everywhere: "Showing 1–25 of 37 bids", "Showing 1 of 1 bid".
 * The total is the server's filtered total. When search or the client filter
 * narrows the page further (they run on this page only), the exact total is
 * known only if the page holds every match; otherwise the footer says so.
 */
export function bidsFooterLabel({ page, pageSize, total, hasMore, pageCount, shownCount }: BidsFooterInput): string {
  const narrowed = shownCount < pageCount
  if (narrowed && (page > 1 || hasMore)) {
    return shownCount === 0
      ? 'No matching bids on this page'
      : `Showing ${shownCount} matching ${bidsNoun(shownCount)} on this page`
  }
  const count = narrowed ? shownCount : pageCount
  const filteredTotal = narrowed ? shownCount : total
  if (count === 0) return `Showing 0 of ${filteredTotal} ${bidsNoun(filteredTotal)}`
  const start = narrowed ? 1 : (page - 1) * pageSize + 1
  const end = start + count - 1
  const range = start === end ? `${start}` : `${start}–${end}`
  return `Showing ${range} of ${filteredTotal} ${bidsNoun(filteredTotal)}`
}

/**
 * Exports the rows the table is showing: the current page, after the tab,
 * the filters and search. Not every bid in the organisation.
 */
function exportToCSV(bids: Bid[]) {
  const csv = bidsToCsv(bids)
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = bidsCsvFileName(new Date())
  a.click()
  URL.revokeObjectURL(url)
}

export function BidsTable() {
  const navigate = useNavigate()
  const currentUserId = useAuthStore((state) => state.user?.id ?? null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [editingBid, setEditingBid] = useState<Bid | null>(null)
  const [deletingBid, setDeletingBid] = useState<Bid | null>(null)
  const [showDeleted, setShowDeleted] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const saved = localStorage.getItem('bidcontext:bids:columns')
    return { ...DEFAULT_HIDDEN_COLUMNS, ...(saved ? JSON.parse(saved) as VisibilityState : {}) }
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseBidsTab(searchParams.get(BIDS_TAB_PARAM))

  // Store state and actions
  const {
    bids,
    isLoadingBids,
    error,
    filter,
    pagination,
    users,
    fetchBids,
    fetchUsers,
    setFilter,
    clearError,
    duplicateBid,
    archiveBid,
    unarchiveBid,
    bulkDelete,
    getFilteredBids,
    lifecycleCounts,
  } = useBidsStore()
  const stageFilter = filter.stages ?? []

  // The tab is the lifecycle filter. It is sent to the server and also applied
  // client-side (getFilteredBids), so the tabs still filter against a backend
  // that ignores `lifecycle=`. Stages only exist on active bids, so leaving the
  // Active tab drops the stage filter.
  useEffect(() => {
    setFilter(tab === "active" ? { lifecycles: [tab] } : { lifecycles: [tab], stages: [] })
    void fetchBids({ page: 1 })
  }, [tab, fetchBids, setFilter])

  const handleTabChange = (lifecycle: BidLifecycle) => {
    setRowSelection({})
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (lifecycle === "active") next.delete(BIDS_TAB_PARAM)
        else next.set(BIDS_TAB_PARAM, bidsTabParam(lifecycle))
        return next
      },
      { replace: true }
    )
  }

  useEffect(() => {
    void fetchUsers()
  }, [fetchUsers])

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('bidcontext:bids:columns', JSON.stringify(columnVisibility))
  }, [columnVisibility])

  // Memoize filtered bids to avoid new array reference on every render
  const filteredBids = useMemo(() => getFilteredBids(), [bids, filter])
  const currentUserRole = useMemo(
    () => users.find((u) => u.id === currentUserId)?.role ?? null,
    [users, currentUserId]
  )
  const canWrite = currentUserRole !== 'viewer'

  // Unique client names for filter dropdown
  const uniqueClients = useMemo(
    () => [...new Set(bids.map((b) => b.client).filter(Boolean))].sort(),
    [bids]
  )

  // Handle search via store filter
  const handleSearchChange = (value: string) => {
    setFilter({ search: value || undefined })
  }

  // Handle filter changes from popover
  // Stage, priority, deadline and value filters go to the server too, so the
  // footer's total is the filtered total and paging walks the matches.
  const handleFilterChange = (filters: BidsFilters) => {
    setFilter(filters)
    void fetchBids({ page: 1 })
  }

  // Handle client filter toggle
  const handleClientToggle = (client: string) => {
    const current = filter.clients ?? []
    const updated = current.includes(client)
      ? current.filter((c) => c !== client)
      : [...current, client]
    setFilter({ clients: updated })
  }

  // Define columns using TanStack Table's ColumnDef
  const columns = useMemo<ColumnDef<Bid>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => {
              if (!canWrite) return
              table.toggleAllPageRowsSelected(!!value)
            }}
            aria-label="Select all"
            disabled={!canWrite}
          />
        ),
        cell: ({ row, table }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
            disabled={!canWrite || row.original.canEdit === false}
            className={cn(
              "transition-opacity focus-visible:opacity-100 group-hover:opacity-100",
              row.getIsSelected() || table.getSelectedRowModel().rows.length > 0
                ? "opacity-100"
                : "opacity-0"
            )}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "title",
        header: ({ column }) => (
          <SortableHeader column={column}>Bid Title</SortableHeader>
        ),
        cell: ({ row }) => (
          <span
            className="text-sm truncate block max-w-[400px]"
            title={row.getValue("title")}
          >
            {row.getValue("title")}
          </span>
        ),
        enableHiding: false,
      },
      {
        accessorKey: "client",
        header: ({ column }) => (
          <SortableHeader column={column}>Client</SortableHeader>
        ),
        cell: ({ row }) => (
          <p
            className="text-sm text-muted-foreground truncate max-w-[360px]"
            title={row.getValue("client")}
          >
            {row.getValue("client")}
          </p>
        ),
      },
      {
        accessorKey: "deadline",
        header: ({ column }) => (
          <SortableHeader column={column}>Deadline</SortableHeader>
        ),
        cell: ({ row }) => {
          const bid = row.original
          if (!bid.deadline) {
            return <span className="text-sm text-muted-foreground">—</span>
          }
          const isOverdue = isBidOverdue(bid)
          const relative = getRelativeDeadline(bid.deadline, isOverdue)
          const absolute = withDeadlineTime(formatDeadline(bid.deadline), bid)
          if (!relative) {
            return <span className="text-sm text-muted-foreground">{absolute}</span>
          }
          return (
            <div className="flex flex-col leading-tight whitespace-nowrap">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm",
                  relative.urgency === 'overdue' && "text-destructive",
                  relative.urgency !== 'overdue' && "text-foreground font-medium"
                )}
              >
                {relative.urgency === 'overdue' ? (
                  <AlertTriangle className="h-3.5 w-3.5" />
                ) : (
                  <Clock
                    className={cn(
                      "h-3.5 w-3.5",
                      relative.urgency === 'urgent' && "text-foreground",
                      relative.urgency === 'normal' && "text-muted-foreground",
                      relative.urgency === 'far' && "text-muted-foreground"
                    )}
                  />
                )}
                {relative.label}
              </span>
              <span className="text-[11px] text-muted-foreground mt-0.5">
                {absolute}
              </span>
            </div>
          )
        },
      },
      {
        accessorKey: "tags",
        header: "Tags",
        enableSorting: false,
        cell: ({ row }) => {
          const tags = row.original.tags ?? []
          return (
            <div className="flex flex-wrap gap-1 max-w-[150px]">
              {tags.length > 0 ? (
                tags.slice(0, 3).map((tag, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-meta">—</span>
              )}
              {tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{tags.length - 3}</span>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: "ownerId",
        header: "Owner",
        enableSorting: false,
        cell: ({ row }) => {
          const ownerId = row.original.ownerId
          if (!ownerId) return <span className="text-sm text-muted-foreground">—</span>
          const owner = users.find((u) => u.id === ownerId)
          return (
            <span className="text-sm text-muted-foreground truncate">
              {getUserDisplayName(
                { userId: ownerId, fullName: owner?.fullName, email: owner?.email },
                currentUserId
              )}
            </span>
          )
        },
      },
      {
        accessorKey: "priority",
        header: ({ column }) => (
          <SortableHeader column={column}>Priority</SortableHeader>
        ),
        cell: ({ row }) => {
          const priority = String(row.getValue("priority")) as keyof typeof priorityConfig
          const config = priorityConfig[priority] ?? { label: priority, dotVar: "--muted-foreground" }
          return <DotPill label={config.label} dotVar={config.dotVar} className="min-w-[5.5rem] justify-center" />
        },
        sortingFn: (rowA, rowB) => {
          const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }
          const a = priorityOrder[rowA.original.priority] ?? 4
          const b = priorityOrder[rowB.original.priority] ?? 4
          return a - b
        },
      },
      {
        accessorKey: "value",
        header: ({ column }) => (
          <SortableHeader column={column}>Value</SortableHeader>
        ),
        cell: ({ row }) => (
          <p className="text-sm text-muted-foreground text-center">{formatValue(row.original.value)}</p>
        ),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.value ?? 0
          const b = rowB.original.value ?? 0
          return a - b
        },
      },
      {
        id: "stage",
        accessorFn: (bid) => bidPositionRank(bidLifecycleOf(bid)),
        header: ({ column }) => (
          <SortableHeader column={column}>Stage</SortableHeader>
        ),
        cell: ({ row }) => <BidStagePill bid={row.original} />,
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <SortableHeader column={column}>Created</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDeadline(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => void navigate(`/app/bids/${row.original.id}`)}>
                View Details
              </DropdownMenuItem>
              {canWrite && row.original.canEdit !== false && (
                <>
                  <DropdownMenuItem onClick={() => setEditingBid(row.original)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      void duplicateBid(row.original.id)
                      toast.success("Bid duplicated")
                    }}
                  >
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      void archiveBid(row.original.id)
                      toast('Bid archived', {
                        action: {
                          label: 'Undo',
                          onClick: () => void unarchiveBid(row.original.id),
                        },
                      })
                    }}
                  >
                    Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-muted-foreground focus:text-destructive focus:bg-destructive/10"
                    onClick={() => setDeletingBid(row.original)}
                  >
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableHiding: false,
      },
    ],
    [navigate, users, duplicateBid, archiveBid, unarchiveBid, canWrite, currentUserId]
  )

  // Initialize TanStack Table
  // Note: filtering (status, priority, search, deadline, value) is handled by
  // getFilteredBids() in the store — no need for TanStack's globalFilter/getFilteredRowModel
  const table = useReactTable({
    data: filteredBids,
    columns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })
  // Compute selected IDs
  const selectedIds = Object.keys(rowSelection)
    .map((idx) => table.getRowModel().rows[Number(idx)]?.original.id)
    .filter(Boolean) as string[]

  // Keyboard shortcut: Command+K (Mac) or Ctrl+K (Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  const handleRowClick = (bidId: string) => {
    void navigate(`/app/bids/${bidId}`)
  }

  // Get all column IDs except select, title, and actions
  const toggleableColumns = table
    .getAllColumns()
    .filter((col) => col.getCanHide())

  // Show loading state
  if (isLoadingBids && bids.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading bids...</p>
      </div>
    )
  }

  // Show error state
  if (error && bids.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="mt-2 text-sm text-destructive">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => { clearError(); void fetchBids() }}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Fixed header: error + toolbar + bulk actions */}
      <div className="shrink-0">
        {/* Error Banner (non-blocking) */}
        {error && bids.length > 0 && (
          <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-2 flex items-center justify-between">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="ghost" size="sm" onClick={clearError}>Dismiss</Button>
          </div>
        )}

        <BidsLifecycleTabs active={tab} counts={lifecycleCounts} onChange={handleTabChange} />

        {/* Toolbar - search left, actions right */}
        <div className="bg-background px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Search bar + client filter */}
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search bids..."
                value={filter.search ?? ""}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 pr-16 h-8"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </div>
            </div>
            {uniqueClients.length > 0 && (
              <ClientFilterCombobox
                clients={uniqueClients}
                selected={filter.clients ?? []}
                onToggle={handleClientToggle}
                onClear={() => setFilter({ clients: [] })}
              />
            )}
          </div>
          {/* Actions */}
          <div className="flex items-center gap-2">
            <BidsFilterPopover
              showStages={tab === "active"}
              filters={{
                stages: stageFilter,
                priorities: filter.priorities,
                ...(filter.deadlineFrom && { deadlineFrom: filter.deadlineFrom }),
                ...(filter.deadlineTo && { deadlineTo: filter.deadlineTo }),
                ...(filter.valueMin !== undefined && { valueMin: filter.valueMin }),
                ...(filter.valueMax !== undefined && { valueMax: filter.valueMax }),
              }}
              onChange={handleFilterChange}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Columns">
                  <Columns3 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48" align="end">
                <div className="space-y-2">
                  <p className="text-sm font-medium mb-2">Toggle columns</p>
                  {toggleableColumns.map((column) => (
                    <div key={column.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`col-${column.id}`}
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      />
                      <label
                        htmlFor={`col-${column.id}`}
                        className="text-sm cursor-pointer flex-1 capitalize"
                      >
                        {column.id}
                      </label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => exportToCSV(filteredBids)}
              title="Export CSV"
            >
              <Download className="h-4 w-4" />
            </Button>
            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowDeleted(true)}
                title="Recently deleted"
              >
                <History className="h-4 w-4" />
              </Button>
            )}
            {canWrite && <CreateBidDialog onSuccess={() => void fetchBids()} />}
          </div>
        </div>
      </div>

      </div>

      {/* Table — scrolls vertically and horizontally within its own viewport */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 pt-2">
        <div className="flex-1 min-h-0 overflow-auto scrollbar-always">
          <div className="min-w-full w-max">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-border">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      aria-sort={
                        !header.column.getCanSort()
                          ? undefined
                          : header.column.getIsSorted() === "asc"
                            ? "ascending"
                            : header.column.getIsSorted() === "desc"
                              ? "descending"
                              : "none"
                      }
                      className="px-4 py-3 text-left text-sm font-medium text-foreground bg-background"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-border/60">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-accent/30 transition-all duration-200 group cursor-pointer"
                  onClick={() => handleRowClick(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-2.5">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {table.getRowModel().rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              {filter.search || stageFilter.length > 0 || filter.priorities.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-2">No bids match your filters.</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilter({ stages: [], priorities: [] })}
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <FolderOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h3 className="text-sm font-medium mb-1">No bids yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">Track your tender and RFP responses from lead through to submission.</p>
                  {canWrite && <CreateBidDialog onSuccess={() => void fetchBids()} />}
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Pagination — pinned below the scroll viewport */}
      {pagination.total > 0 && (
        <div className="shrink-0 flex items-center justify-between px-6 py-3">
          <p className="text-sm text-muted-foreground">
            {bidsFooterLabel({
              page: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              hasMore: pagination.hasMore,
              pageCount: bids.length,
              shownCount: filteredBids.length,
            })}
          </p>
          <div className="flex items-center gap-2">
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(v) => void fetchBids({ pageSize: Number(v), page: 1 })}
            >
              <SelectTrigger className="w-[80px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => void fetchBids({ page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={!pagination.hasMore}
              onClick={() => void fetchBids({ page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Bulk actions — floating snackbar, shared with the extraction tables */}
      {canWrite && (
        <BulkActionsBar
          count={selectedIds.length}
          onClear={() => setRowSelection({})}
        >
          {/* R4: no bulk "Move to". A bid moves through its stage control,
              which captures the outcome when it closes. */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-background hover:bg-background/15 hover:text-background gap-1"
            onClick={() => {
              const ids = selectedIds
              setRowSelection({})
              void Promise.all(ids.map((id) => archiveBid(id)))
                .then(() => toast.success(`${ids.length} bid${ids.length > 1 ? 's' : ''} archived`))
                .catch(() => toast.error('Some bids could not be archived'))
            }}
          >
            <Archive className="h-3.5 w-3.5" />
            Archive
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
            onClick={() => {
              void bulkDelete(selectedIds)
              setRowSelection({})
              toast.success(`${selectedIds.length} bid${selectedIds.length > 1 ? 's' : ''} deleted`)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </BulkActionsBar>
      )}

      {/* Edit Bid Dialog (controlled by editingBid state) */}
      {editingBid && (
        <EditBidDialog
          bid={editingBid}
          open={!!editingBid}
          onOpenChange={(open) => !open && setEditingBid(null)}
          onSuccess={() => {
            setEditingBid(null)
            void fetchBids()
          }}
        />
      )}

      {/* Delete Bid Dialog (controlled by deletingBid state) */}
      {deletingBid && (
        <DeleteBidDialog
          bid={deletingBid}
          open={!!deletingBid}
          onOpenChange={(open) => !open && setDeletingBid(null)}
          onSuccess={() => {
            setDeletingBid(null)
            void fetchBids()
          }}
        />
      )}

      <RecentlyDeletedBidsDialog open={showDeleted} onOpenChange={setShowDeleted} />
    </div>
  )
}
